import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, doc, getDoc, setDoc, where } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import MDTypography from '../components/MDTypography';
import MDInput from '../components/MDInput';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext';

const TeamRunningNetPL = ({ userID, tradeAccountID = 'default' }) => {
  const {
    selectedPeriod, setSelectedPeriod,
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    selectedTag, setSelectedTag,
    showMissedTrades, setShowMissedTrades,
  } = usePeriod();
  const [trades, setTrades] = useState([]);
  const [accountName, setAccountName] = useState(tradeAccountID);
  const [uniqueTags, setUniqueTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const years = Array.from({ length: 2 }, (_, i) => new Date().getFullYear() - i);

  // Fetch account name
  useEffect(() => {
    const fetchAccountName = async () => {
      if (tradeAccountID === 'default') {
        setAccountName('Default');
        return;
      }
      try {
        const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
        if (accountDoc.exists()) {
          setAccountName(accountDoc.data().name || tradeAccountID);
        }
      } catch (error) {
        console.error('TeamRunningNetPL: Error fetching account name:', error);
      }
    };
    fetchAccountName();
  }, [userID, tradeAccountID]);

  // Fetch trades
  useEffect(() => {
    const fetchTrades = async () => {
      setLoading(true);
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', showMissedTrades ? 'Missed' : 'Closed')
        );
        const tradeSnapshot = await getDocs(tradesQuery);
        const tradesData = tradeSnapshot.docs.map((doc) => ({
          id: doc.id,
          date: doc.data().date instanceof Object ? doc.data().date.toDate() : new Date(doc.data().date),
          returns: Number(doc.data().returns) || 0,
          tags: doc.data().tags || [],
          status: doc.data().status,
        }));
        const tags = new Set();
        tradesData.forEach((trade) => {
          if (Array.isArray(trade.tags)) {
            trade.tags.forEach((tag) => tags.add(tag));
          }
        });
        console.log('TeamRunningNetPL: Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, 'Trades:', tradesData);
        setTrades(tradesData);
        setUniqueTags(Array.from(tags));
        setHasData(tradesData.length > 0);
      } catch (error) {
        console.error('TeamRunningNetPL: Error fetching trades:', error);
        setTrades([]);
        setUniqueTags([]);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    if (userID && tradeAccountID) {
      fetchTrades();
    }
  }, [userID, tradeAccountID, showMissedTrades]);

  // Load and save user preferences
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const prefRef = doc(db, `Users/${userID}/preferences/periodSettings`);
        const prefDoc = await getDoc(prefRef);
        const validPeriods = [
          'today', 'this-week', 'this-month', 'this-year',
          'pick-month', 'pick-year', 'pick-month-year', 'all-time',
        ];
        const currentDate = new Date();

        if (prefDoc.exists()) {
          const prefs = prefDoc.data();
          const period = validPeriods.includes(prefs.defaultPeriod) ? prefs.defaultPeriod : 'all-time';
          const month = (prefs.defaultMonth >= 1 && prefs.defaultMonth <= 12) ? prefs.defaultMonth : currentDate.getMonth() + 1;
          const year = prefs.defaultYear || currentDate.getFullYear();
          const tag = prefs.defaultTag || 'all';

          setSelectedPeriod(period);
          setSelectedMonth(month);
          setSelectedYear(year);
          setSelectedTag(tag);
          setShowMissedTrades(prefs.showMissedTrades || false);

          console.log('TeamRunningNetPL: Loaded preferences:', { period, month, year, tag, showMissedTrades: prefs.showMissedTrades });
        } else {
          const defaults = {
            defaultPeriod: 'all-time',
            defaultMonth: currentDate.getMonth() + 1,
            defaultYear: currentDate.getFullYear(),
            defaultTag: 'all',
            showMissedTrades: false,
          };
          await setDoc(prefRef, defaults);

          setSelectedPeriod(defaults.defaultPeriod);
          setSelectedMonth(defaults.defaultMonth);
          setSelectedYear(defaults.defaultYear);
          setSelectedTag(defaults.defaultTag);
          setShowMissedTrades(defaults.showMissedTrades);

          console.log('TeamRunningNetPL: Set default preferences:', defaults);
        }
      } catch (error) {
        console.error('TeamRunningNetPL: Error in loadUserPreferences:', error);
      }
    };

    loadUserPreferences();
  }, [userID]);

  useEffect(() => {
    const saveUserPreferences = async () => {
      try {
        const prefRef = doc(db, `Users/${userID}/preferences/periodSettings`);
        const currentPrefs = {
          defaultPeriod: selectedPeriod,
          defaultMonth: selectedMonth,
          defaultYear: selectedYear,
          defaultTag: selectedTag,
          showMissedTrades,
        };
        console.log('TeamRunningNetPL: Saving preferences:', currentPrefs);
        await setDoc(prefRef, currentPrefs, { merge: true });
      } catch (error) {
        console.error('TeamRunningNetPL: Error saving preferences:', error);
      }
    };

    saveUserPreferences();
  }, [selectedPeriod, selectedMonth, selectedYear, selectedTag, showMissedTrades]);

  const filterTrades = (entries) => {
    const filtered = entries.filter((entry) => {
      const entryDate = new Date(entry.date);
      const matchesPeriod = true; // Include all trades to match TeamWinLossStats
      const matchesTag = selectedTag === 'all' || (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      console.log(`TeamRunningNetPL: Filter trade ${entry.id}, date: ${entryDate.toISOString()}, returns: ${entry.returns}, periodMatch: ${matchesPeriod}, tagMatch: ${matchesTag}, status: ${entry.status}`);
      return matchesPeriod && entry.status === (showMissedTrades ? 'Missed' : 'Closed') && matchesTag;
    });
    console.log('TeamRunningNetPL: Filtered trades:', filtered);
    return filtered;
  };

  const processData = useMemo(() => {
    const filteredTrades = filterTrades(trades);
    const netPLData = [];
    let cumulativePL = 0;

    const sortedTrades = filteredTrades.sort((a, b) => new Date(a.date) - new Date(b.date));

    sortedTrades.forEach((trade) => {
      const returns = parseFloat(trade.returns);
      if (isFinite(returns)) {
        cumulativePL += returns;
        netPLData.push({
          date: trade.date,
          netPL: cumulativePL,
        });
        console.log(`TeamRunningNetPL: Processed trade ${trade.id}, returns: ${returns}, cumulativePL: ${cumulativePL}`);
      } else {
        console.warn(`TeamRunningNetPL: Skipping invalid returns for trade ${trade.id}: ${trade.returns}`);
      }
    });

    console.log('TeamRunningNetPL: Final Net P&L data:', netPLData);
    return { netPLData, cumulativePL };
  }, [trades, selectedTag, showMissedTrades]);

  const data = {
    labels: processData.netPLData.map((entry) => new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Cumulative Net P&L ($)',
        data: processData.netPLData.map((entry) => entry.netPL),
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        type: 'category',
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        title: { display: true, text: 'Net P&L ($)' },
        ticks: {
          callback: (value) => `$${value.toFixed(2)}`,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `Net P&L: $${context.parsed.y.toFixed(2)}`,
        },
      },
    },
  };

  const handlePeriodChange = (value) => {
    console.log('TeamRunningNetPL: Period changed to:', value);
    setSelectedPeriod(value);
  };

  const handleMonthChange = (value) => {
    console.log('TeamRunningNetPL: Month changed to:', value);
    setSelectedMonth(value);
  };

  const handleYearChange = (value) => {
    console.log('TeamRunningNetPL: Year changed to:', value);
    setSelectedYear(value);
  };

  const handleTagChange = (value) => {
    console.log('TeamRunningNetPL: Tag changed to:', value);
    setSelectedTag(value);
  };

  const handleToggleMissedTrades = (event) => {
    const newValue = event.target.checked;
    console.log('TeamRunningNetPL: Toggle changed to:', newValue);
    setShowMissedTrades(newValue);
  };

  const currencyFormatter = (value) => `$${value.toFixed(2)}`;

  const tooltipContent = `The Running Net P&L chart shows the cumulative profit and loss over time for ${accountName}. Select time periods and tags to filter trades, and toggle missed trades to analyze potential outcomes.`;

  // Log P&L calculation for title
  const totalPL = processData.netPLData.length > 0 ? processData.cumulativePL : 0;
  console.log('TeamRunningNetPL: Title P&L calculation:', {
    processDataLength: processData.netPLData.length,
    finalNetPL: processData.netPLData.length > 0 ? processData.netPLData[processData.netPLData.length - 1].netPL : 0,
    cumulativePL: processData.cumulativePL,
    displayedTotal: totalPL,
  });

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }
    if (!hasData || processData.netPLData.length === 0) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No trading data available
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Complete some trades to see your Net P&L
          </MDTypography>
        </MDBox>
      );
    }
    return (
      <MDBox height="100%">
        <Line data={data} options={chartOptions} />
      </MDBox>
    );
  };

  return (
    <Card sx={{ minHeight: '300px', display: 'flex', flexDirection: 'column' }}>
      <MDBox display="flex" flexDirection="column" pt={1} px={2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <TroubleshootIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Running Net P&L {tradeAccountID === 'default' ? '' : `(${accountName})`} {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit">
              {currencyFormatter(totalPL)}
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider sx={{ my: 1 }} />
      </MDBox>
      <MDBox px={2} pb={2} sx={{ flexGrow: 1 }}>
        <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <MDBox display="flex" alignItems="center" sx={{ minWidth: '180px' }}>
            <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>
              Period:
            </MDTypography>
            <MDInput
              select
              value={selectedPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
              size="small"
              sx={{ width: '120px' }}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="this-week">This Week</MenuItem>
              <MenuItem value="this-month">This Month</MenuItem>
              <MenuItem value="this-year">This Year</MenuItem>
              <MenuItem value="pick-month">Pick Month</MenuItem>
              <MenuItem value="pick-year">Pick Year</MenuItem>
              <MenuItem value="pick-month-year">Month & Year</MenuItem>
              <MenuItem value="all-time">All Time</MenuItem>
            </MDInput>
          </MDBox>
          {(selectedPeriod === 'pick-month' || selectedPeriod === 'pick-month-year') && (
            <MDBox display="flex" alignItems="center" sx={{ minWidth: '120px' }}>
              <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>
                Month:
              </MDTypography>
              <MDInput
                select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                size="small"
                sx={{ width: '80px' }}
              >
                {months.map((month, index) => (
                  <MenuItem key={index} value={index + 1}>{month}</MenuItem>
                ))}
              </MDInput>
            </MDBox>
          )}
          {(selectedPeriod === 'pick-year' || selectedPeriod === 'pick-month-year') && (
            <MDBox display="flex" alignItems="center" sx={{ minWidth: '120px' }}>
              <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>
                Year:
              </MDTypography>
              <MDInput
                select
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                size="small"
                sx={{ width: '80px' }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </MDInput>
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" sx={{ minWidth: '180px' }}>
            <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>
              Tag:
            </MDTypography>
            <MDInput
              select
              value={selectedTag}
              onChange={(e) => handleTagChange(e.target.value)}
              size="small"
              sx={{ width: '120px' }}
            >
              <MenuItem value="all">All Tags</MenuItem>
              {uniqueTags.map((tag) => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </MDInput>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={showMissedTrades}
                  onChange={handleToggleMissedTrades}
                  color="primary"
                  size="small"
                />
              }
              label={<MDTypography variant="caption" color="text">Missed Trades</MDTypography>}
              sx={{ m: 0 }}
            />
          </MDBox>
        </MDBox>
        {renderContent()}
      </MDBox>
    </Card>
  );
};

TeamRunningNetPL.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default TeamRunningNetPL;